import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Boxes, PackageCheck } from 'lucide-react';

/**
 * ProductionMaterialsPanel — shows BOMs used + inventory consumed for a PO.
 *
 * For each PO line:
 *   - The BOM referenced (name + revision)
 *   - BOM required components (per-unit × PO qty)
 *   - What was actually consumed on the shop floor (via the linked WO's
 *     component_consumption records) with lot numbers
 */
const ProductionMaterialsPanel = ({ api, poId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!poId || poId === 'new') { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api().get(`/production-orders/${poId}/materials`);
      setData(r.data);
    } catch { /* non-blocking */ }
    finally { setLoading(false); }
  }, [api, poId]);
  useEffect(() => { load(); }, [load]);

  if (!poId || poId === 'new') return null;

  return (
    <Card className="industrial-card" data-testid="po-materials-panel">
      <CardHeader className="border-b border-slate-100 pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Boxes className="w-4 h-4 text-slate-600" />
          BOMs Used &amp; Inventory Consumed
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-4 text-sm text-slate-500">Loading materials…</div>
        ) : !data || !(data.lines || []).length ? (
          <div className="p-4 text-sm text-slate-500 italic">No BOM lines on this Production Order yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {(data.lines || []).map((ln) => (
              <div key={ln.line_id} className="p-4 space-y-3" data-testid={`po-mat-line-${ln.line_id}`}>
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-semibold text-sm flex items-center gap-2">
                      <span>{ln.bom_name || `BOM ${ln.bom_id}`}</span>
                      {ln.bom_revision && <Badge variant="outline" className="rounded-sm text-xs">Rev {ln.bom_revision}</Badge>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      PO qty: <span className="mono">{ln.quantity}</span>
                      {ln.work_order_number && <> · WO <strong className="mono">{ln.work_order_number}</strong></>}
                      {ln.work_order_status && <> · <Badge variant="outline" className="rounded-sm text-xs">{ln.work_order_status}</Badge></>}
                      {ln.units_completed > 0 && <> · <PackageCheck className="w-3 h-3 inline" /> {ln.units_completed} unit{ln.units_completed > 1 ? 's' : ''} built</>}
                    </div>
                  </div>
                </div>

                <div className="rounded-sm border border-slate-200 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Component SKU</TableHead>
                        <TableHead className="text-xs">Item Name</TableHead>
                        <TableHead className="text-xs text-right">Required (per PO)</TableHead>
                        <TableHead className="text-xs text-right">Consumed (shop floor)</TableHead>
                        <TableHead className="text-xs">Lots Used</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(ln.components || []).map((c, idx) => {
                        const diff = Number(c.consumed_qty || 0) - Number(c.required_qty || 0);
                        const complete = c.consumed_qty > 0 && diff >= 0;
                        const partial = c.consumed_qty > 0 && diff < 0;
                        return (
                          <TableRow key={`${ln.line_id}-${c.component_sku}-${idx}`} data-testid={`po-mat-comp-${c.component_sku}`}>
                            <TableCell className="mono text-xs">{c.component_sku}</TableCell>
                            <TableCell className="text-xs">{c.item_name || <span className="text-slate-400">—</span>}</TableCell>
                            <TableCell className="text-xs text-right mono">{Number(c.required_qty || 0).toFixed(2)}</TableCell>
                            <TableCell className="text-xs text-right mono font-semibold">{Number(c.consumed_qty || 0).toFixed(2)}</TableCell>
                            <TableCell className="text-xs mono">
                              {(c.lots || []).length ? (c.lots || []).map((l) => `${l.lot}${l.qty ? ` (${l.qty})` : ''}`).join(', ') : <span className="text-slate-400">—</span>}
                            </TableCell>
                            <TableCell className="text-xs">
                              {complete && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border rounded-sm text-xs">Complete</Badge>}
                              {partial && <Badge className="bg-amber-100 text-amber-800 border-amber-200 border rounded-sm text-xs">Partial</Badge>}
                              {!c.consumed_qty && <Badge variant="outline" className="rounded-sm text-xs text-slate-500">Pending</Badge>}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {!(ln.components || []).length && (
                        <TableRow><TableCell colSpan={6} className="text-center text-xs text-slate-400 py-3">— no components defined on this BOM —</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductionMaterialsPanel;
